import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faComments,
  faPaperPlane,
  faTrash,
  faPhone,
  faClock,
  faInbox,
  faUser,
  faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import { useDashboardAuth } from '../../context/DashboardAuthContext';
import api from '../../services/api';
import './ChatInbox.css';

const POLL_INTERVAL = 6000;

const ChatInbox = () => {
  const { hasPermission, isSuperAdmin } = useDashboardAuth();
  const canManage = isSuperAdmin() || hasPermission('chat.manage');
  const [threads, setThreads] = useState([]);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const fetchThreads = useCallback(async (silent = false) => {
    if (!silent) setLoadingThreads(true);
    try {
      const response = await api.get('/dashboard-admin/chat-threads');
      setThreads(response.data || []);
    } catch (error) {
      console.error('Error fetching chat threads:', error);
      if (!silent) setThreads([]);
    } finally {
      if (!silent) setLoadingThreads(false);
    }
  }, []);

  const fetchMessages = useCallback(async (threadId, silent = false) => {
    if (!silent) setLoadingMessages(true);
    try {
      const response = await api.get(`/dashboard-admin/chat-threads/${threadId}/messages`);
      setMessages(response.data || []);
      setThreads(prev => prev.map(t => t.id === threadId ? { ...t, unread_count: 0 } : t));
    } catch (error) {
      console.error('Error fetching thread messages:', error);
      if (!silent) setMessages([]);
    } finally {
      if (!silent) setLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  // Keep the thread list fresh so new conversations / unread counts show up without a reload.
  useEffect(() => {
    const timer = setInterval(() => fetchThreads(true), POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [fetchThreads]);

  // Poll the open conversation so new visitor messages appear without reselecting it.
  useEffect(() => {
    if (!selectedId) return;
    const timer = setInterval(() => fetchMessages(selectedId, true), POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [selectedId, fetchMessages]);

  const openThread = (thread) => {
    setSelectedId(thread.id);
    fetchMessages(thread.id);
  };

  const closeThread = () => {
    setSelectedId(null);
    setMessages([]);
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleReply = async (e) => {
    e.preventDefault();
    if (!reply.trim() || !selectedId) return;
    setSending(true);
    try {
      const response = await api.post(`/dashboard-admin/chat-threads/${selectedId}/reply`, { message: reply.trim() });
      setMessages(prev => [...prev, response.data]);
      setReply('');
      fetchThreads();
    } catch (error) {
      console.error('Error sending reply:', error);
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (threadId) => {
    if (!window.confirm('Supprimer cette conversation ?')) return;
    try {
      await api.delete(`/dashboard-admin/chat-threads/${threadId}`);
      setThreads(prev => prev.filter(t => t.id !== threadId));
      if (selectedId === threadId) {
        setSelectedId(null);
        setMessages([]);
      }
    } catch (error) {
      console.error('Error deleting thread:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const selectedThread = threads.find(t => t.id === selectedId);

  return (
    <div className="chat-inbox-page">
      <div className="page-header">
        <div className="header-title">
          <h1><FontAwesomeIcon icon={faComments} /> Chat</h1>
          <p>Conversations démarrées par les visiteurs du site</p>
        </div>
      </div>

      <div className={`chat-inbox-layout ${selectedThread ? 'has-selection' : ''}`}>
        <div className="chat-thread-list">
          {loadingThreads ? (
            <div className="loading">
              <div className="spinner"></div>
              <p>Chargement...</p>
            </div>
          ) : threads.length === 0 ? (
            <div className="empty-state">
              <FontAwesomeIcon icon={faInbox} />
              <p>Aucune conversation pour le moment</p>
            </div>
          ) : (
            threads.map(thread => (
              <button
                key={thread.id}
                className={`thread-item ${selectedId === thread.id ? 'active' : ''}`}
                onClick={() => openThread(thread)}
              >
                <div className="thread-avatar"><FontAwesomeIcon icon={faUser} /></div>
                <div className="thread-info">
                  <div className="thread-info__top">
                    <span className="thread-name">{thread.name}</span>
                    <span className="thread-time">{formatDate(thread.last_message_at)}</span>
                  </div>
                  <p className="thread-preview">{thread.last_message || 'Nouvelle conversation'}</p>
                </div>
                {Number(thread.unread_count) > 0 && (
                  <span className="thread-badge">{thread.unread_count}</span>
                )}
              </button>
            ))
          )}
        </div>

        <div className="chat-conversation">
          {!selectedThread ? (
            <div className="empty-state">
              <FontAwesomeIcon icon={faComments} />
              <p>Sélectionnez une conversation</p>
            </div>
          ) : (
            <>
              <div className="chat-conversation__header">
                <button className="btn-back" onClick={closeThread} aria-label="Retour aux conversations">
                  <FontAwesomeIcon icon={faArrowLeft} />
                </button>
                <div>
                  <h3>{selectedThread.name}</h3>
                  <span><FontAwesomeIcon icon={faPhone} /> {selectedThread.phone}</span>
                </div>
                {canManage && (
                  <button className="btn-delete" onClick={() => handleDelete(selectedThread.id)}>
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                )}
              </div>

              <div className="chat-conversation__messages">
                {loadingMessages ? (
                  <div className="loading"><div className="spinner"></div></div>
                ) : (
                  <>
                    {messages.map((m, i) => (
                      <div key={i} className={`chat-bubble ${m.sender}`}>
                        <p>{m.message}</p>
                        <span className="chat-bubble__time">
                          <FontAwesomeIcon icon={faClock} /> {formatDate(m.created_at)}
                        </span>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {canManage && (
                <form className="chat-conversation__reply" onSubmit={handleReply}>
                  <input
                    type="text"
                    placeholder="Écrire une réponse..."
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                  />
                  <button type="submit" disabled={sending || !reply.trim()}>
                    <FontAwesomeIcon icon={faPaperPlane} />
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatInbox;
